import React, { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Employee } from "../../../../../models/DSF/OrganogramAndAppointments";

interface OrganogramNodeData {
  label: string;
  title?: string;
  accessLevel?: string;
  legalReference?: string;
  employees?: Employee[];
  isEmployee?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

const OrganogramNode = memo<NodeProps<OrganogramNodeData>>(({ data, selected }) => {
  const {
    label,
    title,
    legalReference,
    employees = [],
    isEmployee = false,
    isExpanded = true,
    onToggleExpanded,
  } = data;

  const hasEmployees = employees.length > 0;
  const employeeCount = employees.length;
  const displayEmployees = isExpanded ? employees : employees.slice(0, 2);

  // Determine height class using EXACT same logic as organogramUtils.ts
  const shouldUseExpandedHeight = isExpanded && hasEmployees && employeeCount > 2;
  const nodeHeightClass = shouldUseExpandedHeight ? "expanded" : "compact";

  // Debug logging to verify class assignment
  if (employeeCount > 0) {
    console.log(
      `OrganogramNode ${label}: employees=${employeeCount}, isExpanded=${isExpanded}, class=${nodeHeightClass}`
    );
  }

  return (
    <div
      className={`organogram-node ${isEmployee ? "employee-node" : "department-node"} ${nodeHeightClass} ${selected ? "selected" : ""}`}
    >
      {/* Top Handle for incoming connections */}
      <Handle type="target" position={Position.Top} className="handle handle-top" />

      {/* Main Content */}
      <div className="node-content">
        {!isEmployee ? (
          // Appointment/Position Node
          <>
            {/* Position Name */}
            <div className="info-section">
              <div className="info-value primary">{label}</div>
            </div>

            {/* Legal Reference */}
            {legalReference && (
              <div className="info-section">
                <div className="info-value secondary">{legalReference}</div>
              </div>
            )}

            {/* Employee Information - Enhanced with fallback */}
            {(hasEmployees || (employees && employees.length > 0)) && (
              <div className="employees-section">
                <div className="employees-header">
                  {employees.length > 2 && (
                    <button
                      className="toggle-employees-btn"
                      onClick={onToggleExpanded}
                      type="button"
                    >
                      {isExpanded ? "Show Less" : "Show All"}
                    </button>
                  )}
                </div>

                <div className="employees-list">
                  {displayEmployees.map((employee, index) => (
                    <div key={employee.id || index} className="employee-item">
                      <div className="employee-name">
                        {employee.name} {employee.surname}
                      </div>
                      {employee.title && <div className="employee-title">{employee.title}</div>}
                    </div>
                  ))}

                  {!isExpanded && employees.length > 2 && (
                    <div className="employee-item more-employees">
                      +{employees.length - 2} more...
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          // Employee Node
          <>
            <div className="info-section">
              <div className="info-value primary">{label}</div>
            </div>

            {title && (
              <div className="info-section">
                <div className="info-value secondary">{title}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Handle for outgoing connections */}
      <Handle type="source" position={Position.Bottom} className="handle handle-bottom" />
    </div>
  );
});

OrganogramNode.displayName = "OrganogramNode";

export default OrganogramNode;
